import { useDispatch, useSelector } from 'react-redux';

import { createSelector, createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { ChargerType, Parking } from '../domain/parking';

import { setLoader, toggleToast, unsetLoader } from '@/common/application/toastSlice';
import { RequestTypeEnum } from '@/common/domain/toast';
import useGroupApi from '@/group/data/groupApi';
import { createEmptyPromise, type SelfResolvingPromise } from '@/group/data/groupStore';
import { EnumRelation, type Group } from '@/group/domain/group';
import useParkingApi from '@/parking/data/parkingApi';


export interface ParkingStore {
    isLoading: boolean;
    contextParkingMap: {[key: string]: Parking},
    contextChargerTypeMap: {[key: string]: ChargerType},
}

const initialState: ParkingStore = {
    contextParkingMap: {},
    contextChargerTypeMap: {},
    isLoading: false,
};

const sideState = {
    admKeyLoader: {} as {[key: string]: SelfResolvingPromise<Group> | undefined},
    keyLoader: {} as {[key: string]: SelfResolvingPromise<unknown> | undefined}
};

const parkingStore = createSlice({
    initialState,
    name: 'parkingStore',
    reducers: {
        clearStore() {
            return initialState;
        },
        deleteParking(state, action: PayloadAction<number | string>) {
            if (state.contextParkingMap[String(action.payload)]) delete state.contextParkingMap[String(action.payload)];
        },
        setAdmLoadingKey(state, action: PayloadAction<string>) {
            sideState.admKeyLoader[action.payload] = createEmptyPromise();
        },
        setLoading(state, action: PayloadAction<boolean>) {
            state.isLoading = action.payload;
        },
        clearLoadingKey(state, action: PayloadAction<string>) {
            delete sideState.keyLoader[action.payload];
        },
        setLoadingKey(state, action: PayloadAction<string>) {
            sideState.keyLoader[action.payload] = createEmptyPromise();
            return { ...state };
        },
        failLoadingKey(state, action: PayloadAction<string>) {
            sideState.keyLoader[action.payload]?.reject(new Error('fail to load parking'));
            delete sideState.keyLoader[action.payload];
        },
        unsetAdmLoadingKey(state, action: PayloadAction<{key: string, grp: Group}>) {
            if (action.payload.grp.id) {
                sideState.admKeyLoader[action.payload.key]?.resolve({...action.payload.grp});
                delete sideState.admKeyLoader[action.payload.key];
            }
        },
        upsertParking(state, action: PayloadAction<Parking>) {
            const prk = action.payload;
            if (prk.documentId) state.contextParkingMap[prk.documentId] = {...prk};
            if (prk.documentId && sideState.keyLoader[prk.documentId]) {
                sideState.keyLoader[prk.documentId]?.resolve({...prk});
                delete sideState.keyLoader[prk.documentId];
            }
        },
        upsertParkings(state, action: PayloadAction<Parking[]>) {
            const newParkingsList:{[key: string]: Parking} = {...state.contextParkingMap};
            for (const prk of action.payload) {
                if (prk.documentId) newParkingsList[prk.documentId] = { ...prk};
            }
            if (sideState.keyLoader['allparkings']) {
                sideState.keyLoader['allparkings'].resolve(Object.values(newParkingsList));
                delete sideState.keyLoader['allparkings'];
            }
            state.contextParkingMap = newParkingsList;
        },
        upsertChargerTypes(state, action: PayloadAction<ChargerType[]>) {
            const newChargersList:{[key: string]: ChargerType} = {...state.contextChargerTypeMap};
            for (const prk of action.payload) {
                if (prk.id) newChargersList[prk.id] = { ...prk};
            }
            if (sideState.keyLoader['allchargers']) {
                sideState.keyLoader['allchargers'].resolve(Object.values(newChargersList));
                delete sideState.keyLoader['allchargers'];
            }
            state.contextChargerTypeMap = newChargersList;
        }
    },
    selectors: {
        getParkingAdminById: createSelector([state => state.groupStore?.contextGroupMap], (contextGroupMap): (id: string | number) => Group | undefined => {
            return (id: string | number) => (Object.values<Group>(contextGroupMap ?? {})
                .filter(grp => grp.relatesTo?.some(
                    relate => (
                        relate.relation == EnumRelation.OWNERSHIP 
                    && relate.targetId === String(id) 
                    && relate.targetModel === 'parking'
                    )
                )
                ) ?? []).at(0);
        }),
        getParkings: createSelector([state => state.parkingStore?.contextParkingMap], (contextParkingMap): () => Parking[] => {
            return () => contextParkingMap ? Object.values<Parking>(contextParkingMap) : [];
        }),
        getChargers: createSelector([state => state.parkingStore?.contextChargerTypeMap], (contextChargerTypeMap): () => ChargerType[] => {
            return () => contextChargerTypeMap ? Object.values<ChargerType>(contextChargerTypeMap) : [];
        }),
        getPrkById: createSelector([state => state.parkingStore?.contextParkingMap], (contextParkingMap): (id: string | number) => Parking | null => {
            return (id: string | number) => contextParkingMap ? contextParkingMap[String(id)] : null;
        }) 
    }
});


export const { upsertParkings, setAdmLoadingKey, unsetAdmLoadingKey, failLoadingKey, setLoadingKey, upsertParking, upsertChargerTypes, deleteParking } = parkingStore.actions;
export default parkingStore.reducer;


export function useParkingStore() {

    const { apiGetGroup, apiGetGroupAdminsRT } = useGroupApi();
    
    const { apiGetParkingById, apiGetParkings, apiGetChargerTypes } = useParkingApi();
    
    
    const dispatch = useDispatch();
    const { getPrkById, getParkingAdminById, getParkings: getParkingsSelector, getChargers: getChargersSelector } = parkingStore.getSelectors();

    const findAdminById = useSelector(getParkingAdminById);
    const findPrkById = useSelector(getPrkById);
    const findPrks = useSelector(getParkingsSelector);
    const findChargers = useSelector(getChargersSelector);

    return {
        async deleteParking(id: number | string) {
            return dispatch(deleteParking(id));
        },
       
        async getParkings(force: boolean = false) {
            const foundpkgs = findPrks();
            if (foundpkgs.length > 0 && !force) {
                return foundpkgs;
            } else if (!force && typeof sideState.keyLoader['allparkings'] !== 'undefined') {
                return await sideState.keyLoader['allparkings'] as Parking[];
            } else {
                try {
                    dispatch(setLoadingKey('allparkings'));
                    dispatch(setLoader());
                    const pkgsResp = await apiGetParkings();
                    if (pkgsResp.status.code < 400 && Array.isArray(pkgsResp.data)) {
                        dispatch(upsertParkings(pkgsResp?.data));
                    }
                    dispatch(unsetLoader());
                    return pkgsResp?.data;
                } catch (error: any) {
                    dispatch(unsetLoader());
                    if (error?.status) {
                        dispatch(toggleToast({...error?.status, requestType: RequestTypeEnum.getParkings}));
                    }
                }
                
            }
        },

        async getPrkAdminById(id: string | number) {
            const found = findAdminById(id);
            if (!found && !sideState.admKeyLoader[String(id)]) {
                dispatch(setAdmLoadingKey(String(id)));
                dispatch(setLoader());
                const adminGrp = await apiGetGroupAdminsRT(String(id));
                if (adminGrp?.id && !sideState.keyLoader[String(adminGrp.id)]) {
                    dispatch(setLoadingKey(String(adminGrp.id)));
                    dispatch(unsetAdmLoadingKey({ grp: adminGrp, key: String(id) }));
                    dispatch(unsetLoader());
                }
                return adminGrp;
            } else if (found && !sideState.keyLoader[String(found.id)]) {
                dispatch(setLoadingKey(String(found.id)));
                dispatch(setLoader());
                dispatch(setAdmLoadingKey(String(id)));
                const grpResp = await apiGetGroup(found.documentId!);
                if (grpResp) {
                    dispatch(unsetAdmLoadingKey({ grp: grpResp, key: String(id) }));
                    dispatch(unsetLoader());
                }
                return grpResp;
            } else if (found && found.id && typeof sideState.keyLoader[String(found.id)] !== 'undefined') {
                return await sideState.keyLoader[String(found.id)];
            } else if (!found && typeof sideState.admKeyLoader[String(id)] !== 'undefined') {
                return await sideState.admKeyLoader[String(id)];
            }
            return findAdminById(id);
        },

        async getPrkById(documentId: string, force: boolean = false) {
            
            const loadPkgFromApi = async () => {
                try {
                    dispatch(setLoadingKey(documentId));
                    dispatch(setLoader());

                    const parkingResp = await apiGetParkingById(documentId);
    
                    if (parkingResp.status.code < 400 && parkingResp.data.id) {
                        dispatch(upsertParking(parkingResp?.data));
                        dispatch(unsetLoader());
                        return parkingResp.data;
                    }
                } catch (error: any) {
                    dispatch(unsetLoader());
                    dispatch(failLoadingKey(documentId));
                    if (error?.status) {
                        dispatch(toggleToast({...error?.status, requestType: RequestTypeEnum.getParking}));
                    }
                }
            };
            if (force) {
                return await loadPkgFromApi();
            }
            
            const prkg = findPrkById(documentId);

            if (prkg?.documentId === documentId) return prkg;
            if (typeof sideState.keyLoader[documentId] !== 'undefined') return await sideState.keyLoader[documentId] as Parking;

            return await loadPkgFromApi();
            
        },

        async getChargers(force: boolean = false) {
            const foundchgs = findChargers();
            if (foundchgs.length > 0 && !force) {
                return foundchgs;
            } else if (!force && typeof sideState.keyLoader['allchargers'] !== 'undefined') {
                return await sideState.keyLoader['allchargers'] as ChargerType[];
            } else {
                try {
                    dispatch(setLoadingKey('allchargers'));
                    dispatch(setLoader());
                    const chgsResp = await apiGetChargerTypes();
                    if (chgsResp.status.code < 400 && Array.isArray(chgsResp.data)) {
                        dispatch(upsertChargerTypes(chgsResp?.data));
                    }
                    dispatch(unsetLoader());
                    return chgsResp?.data;
                } catch (error: any) {
                    dispatch(unsetLoader());
                    if (error?.status) {
                        dispatch(toggleToast({...error?.status, requestType: RequestTypeEnum.getParkings}));
                    }
                }
                
            }
        },

    };
}